import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';

import Loader from '../Common/Loader';
import Modal from '../Common/Modal';
import NoAccess from '../Common/NoAccess';

import { logsService } from '../../Services';

import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';
import {ReactComponent as Info} from '../../images/icons/General/info-circle.svg';
import {ReactComponent as Sunset} from '../../images/icons/Weather/sunset.svg';
import {ReactComponent as Sunrise} from '../../images/icons/Weather/sunrise.svg';

const qualification = {
    'TeamLeader': 'Lead diver',
    'ScientificDiver': 'Scientific diver',
    'ScientificDiverInTraining': 'Scientific diver in training',
    'AquariumDiver': 'Aquarium diver',
    'ExhibitDiver': 'Exhibit diver',
    'PresentationDiver': 'Presentation diver',
    'AgaMask': 'Full face mask',
    'SharkDiver': 'Shark diver',
    'NewDiverTrainer': 'New diver trainer',
    'NewDiverTraining': 'New diver training complete',
    'PresentationTrainer': 'Presentation trainer',
    'Volunteer': 'Volunteer',
    'HusbandryInternVolunteer': 'Husbandry Intern/Volunteer',
    'ScientificLeadDiver': 'Lead scientific diver',
    'HookahDiver': 'Hookah diver',
}

export const ScheduleForm = () => {
    const params = useParams();
    const [team, setTeam] = useState([]);
    const [users, setUsers] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [lead, setLead] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [form, setForm] = useState(null);
    const [user, setUser] = useState(null);
    const [copied, setCopied] = useState(false);

    const local = useSelector((state) => state.org.remember);
    const id = useSelector((state) => state[local ? 'localUser' : 'user'].id);

    const copyEmails = () => {
        const emails = users.map(u => u.Email).join('; ');
        navigator.clipboard.writeText(emails);
        setCopied(true);

        setTimeout(() => {
            setCopied(false);
        }, 3000);
    };

    const countUsers = () => {
        return users.filter(u => schedule[u.id]?.WillAttend !== 0).length;
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(false);

            const res = await logsService.getScheduleDate(params);

            if (res.error)
                setError(true);
            else {
                setTeam(res.team);
                setUsers(res.users);
                setSchedule(res.schedule);
                setLead(res.lead);
            }

            setLoading(false);
        };

        fetchData();
    }, [params]);

    return (
        <>
            {loading ? <Loader /> :  (error ? <NoAccess /> : <>
                <h1>Schedule Dives Attendance on {moment(params.date).format('MM/DD/YYYY')}</h1>

                <div className="bordered-content">
                    <div className="header">
                        <h4>{countUsers()} divers</h4>
                        <p>Please review the planned attendance for team {team.Team} ({team.weekday} {team.StartTime} - {team.EndTime})</p>
                        <p>The maximum number of attending divers is limited to 10</p>
                        <div className="buttons">
                            <button className="primary" onClick={copyEmails}>{copied ? 'Emails copied!' : 'Copy emails'}</button>
                            
                            {(users.some((u => u.id === id && !schedule[id])) || (schedule[id] && schedule[id].WillAttend === 1)) ?
                                <button className="primary" onClick={() => setForm(0)}>Cancel attendance</button> :
                                (countUsers() < 10 && <button className="primary" onClick={() => setForm(1)}>Sign up for this shift</button>)}
                        </div>
                    </div>
                    <table id="history-table">
                        <thead>
                            <tr>
                                <th>Diver</th>
                                <th>Email</th>
                                <th>Qualifications</th>
                                <th>Reason of attendance/absence</th>
                                {lead ? <th style={{width: '5%'}}>Status</th> : ''}
                            </tr>
                        </thead>
                        <tbody>
                            {users?.map((user, key) => (
                                <tr key={key} className={schedule[user.id]?.WillAttend === 0 ? 'denied' : (user.TeamLeader ? 'approved' : '')}>
                                    <td>{user.LastName}, {user.FirstName}</td>
                                    <td>{user.Email}</td>
                                    <td>
                                        {Object.keys(qualification)
                                            .filter(key => user[key] === 1)
                                            .map(key => qualification[key])
                                            .join(', ')}
                                    </td>
                                    <td>{schedule[user.id]?.Reason}</td>
                                    <td>{lead && user.id !== id ? 
                                        <>
                                            <span className="pointer desktop" onClick={() => {
                                                setForm(schedule[user.id]?.WillAttend === 0 ? 1 : 0);
                                                setUser(user.id)
                                            }}>{schedule[user.id]?.WillAttend === 0 ? <Sunrise / > : <Sunset />}</span>
                                            <span className="label link mobile" onClick={() => {
                                                setForm(schedule[user.id]?.WillAttend === 0 ? 1 : 0);
                                                setUser(user.id)
                                            }}>{schedule[user.id]?.WillAttend === 0 ? 'Sign up for this shift' : 'Cancel attendance'}</span>
                                        </> : ''}
                                    </td> 
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>)}

            {form !== null && 
                <ModalAttendance 
                    setClose={() => {
                        setForm(null);
                        setUser(null);
                    }} 
                    type={form} 
                    team={team} 
                    setUsers={setUsers} 
                    setSchedule={setSchedule} 
                    date={params.date}
                    user={user}
                />}
        </>
    );
}

export default ScheduleForm;

const ModalAttendance = ({setClose, type, team, setUsers, setSchedule, date, user}) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        setError('');
        setLoading(true);
    
        let message = '';

        if (!reason)
            message = 'Please, enter required fields';

            if (!message) {
                const res = await logsService.scheduleAttendance({
                    teamId: team.TeamID,
                    date: date,
                    reason: reason,
                    type: type,
                    user: user
                });
    
                if (!res.error) {
                    if (res.users) setUsers(res.users);
                    if (res.schedule) setSchedule(res.schedule);
                    setClose();
                } else
                    setError(res.error);
            } else {
                setError(message);
            }
            setLoading(false);
    };
    
    return (
        <Modal setClose={setClose} icon={<Info />}>
            <form onSubmit={handleSubmit}>
                <div className="form-control">
                    <label>Reason</label>
                    <textarea name="reason" value={reason} onChange={(e) => setReason(e.target.value)}></textarea>
                </div>
                <div className="form-submit">
                    <div>
                        <button type="button" className="default" onClick={setClose}>Cancel</button>
                        <button type="submit" className="primary">{loading ? <Loading className="btn-loader" alt="loader" /> : ''}Send</button>
                    </div>
                    {error ? <div className="error">{error}</div> : ''}
                </div>
            </form>
        </Modal>
    )
 }